import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

import useAxios from '../utils/useAxios';
import { SectionCard, ConfirmDialog } from './ui';
import { TRUE_COLORS } from '../theme';

type Account = { available: boolean; connected: boolean; status: string | null; mp_user_id: string | null; problem: string | null };
type Terminal = { id: string; serial: string; model: string; operating_mode: string; checkout: number | null; checkout_label: string | null };
type Register = { id: number; label: string };

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  if (typeof data?.response === 'string') return data.response;
  return fallback;
};

const RETURN_REASONS: Record<string, string> = {
  denied: 'No se conectó: en Mercado Pago no se autorizó a TRUE.',
  expired: 'El enlace para conectar venció. Intenta de nuevo.',
  failed: 'Mercado Pago no completó la conexión. Intenta de nuevo; si sigue fallando, escríbenos.',
};

const MODES: Record<string, { label: string; color: 'success' | 'default' | 'warning' }> = {
  PDV: { label: 'Cobra lo que manda TRUE POS', color: 'success' },
  STANDALONE: { label: 'Cobra sola', color: 'default' },
  SELF_SERVICE: { label: 'Autoservicio', color: 'warning' },
};

/**
 * "Cobros con tarjeta" in Configuración: connect the business's Mercado Pago account and choose the register each
 * Point terminal charges for (the terminal then charges what TRUE POS sends it). Administrators only.
 */
const CardPayments = () => {
  const axios = useAxios();
  const [params, setParams] = useSearchParams();
  const [account, setAccount] = React.useState<Account | null>(null);
  const [terminals, setTerminals] = React.useState<Terminal[] | null>(null);
  const [registers, setRegisters] = React.useState<Register[]>([]);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = React.useState(false);

  // Back from Mercado Pago: ?mercadopago=connected or ?mercadopago=error&reason=…
  React.useEffect(() => {
    const result = params.get('mercadopago');
    if (!result) return;
    // What Mercado Pago answered when it failed: for us to fix it, shown as is.
    const detail = params.get('detail');
    setMessage(result === 'connected'
      ? { type: 'success', text: 'Mercado Pago conectado. Ahora elige la caja de cada terminal.' }
      : { type: 'error', text: `${RETURN_REASONS[params.get('reason') ?? ''] ?? RETURN_REASONS.failed}${detail ? ` Detalle: ${detail}` : ''}` });
    const next = new URLSearchParams(params);
    next.delete('mercadopago');
    next.delete('reason');
    next.delete('detail');
    setParams(next, { replace: true });
  }, [params, setParams]);

  const loadTerminals = React.useCallback(async () => {
    setTerminals(null);
    try {
      const { data } = await axios.get('api/integrations/mercadopago/terminals/');
      setTerminals(data.terminals);
      setRegisters(data.checkouts);
    } catch (err) {
      setTerminals([]);
      setMessage({ type: 'error', text: errorText(err, 'No se pudieron cargar tus terminales.') });
    }
  }, [axios]);

  React.useEffect(() => {
    axios.get('api/integrations/mercadopago/')
      .then(({ data }) => { setAccount(data); if (data.connected) loadTerminals(); })
      .catch(() => setAccount({ available: false, connected: false, status: null, mp_user_id: null, problem: null }));
  }, [axios, loadTerminals]);

  const connect = async () => {
    setBusy('connect');
    try {
      const { data } = await axios.post('api/integrations/mercadopago/connect/', { return_to: `${window.location.origin}${window.location.pathname}` });
      window.location.href = data.url;
    } catch (err) {
      setMessage({ type: 'error', text: errorText(err, 'No se pudo iniciar la conexión con Mercado Pago.') });
      setBusy(null);
    }
  };

  const assign = async (terminal: Terminal, value: string) => {
    setBusy(terminal.id);
    setMessage(null);
    try {
      const { data } = await axios.post('api/integrations/mercadopago/terminals/', { terminal: terminal.id, checkout: value === '' ? null : Number(value) });
      setTerminals(data.terminals);
      const register = registers.find(r => r.id === data.checkout);
      const restart = data.restart ? ' Reinicia la terminal (apágala y enciéndela) para que tome el cambio.' : '';
      setMessage({
        type: 'success',
        text: register
          ? `Listo: la terminal ${terminal.serial} cobra lo que mande ${register.label}.${restart}`
          : `Listo: la terminal ${terminal.serial} vuelve a cobrar sola.${restart}`,
      });
    } catch (err) {
      setMessage({ type: 'error', text: errorText(err, 'No se pudo cambiar la terminal.') });
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    try {
      const { data } = await axios.post('api/integrations/mercadopago/disconnect/');
      setAccount(data);
      setTerminals(null);
      setMessage({ type: 'info', text: 'Mercado Pago desconectado. Tus terminales vuelven a cobrar solas.' });
    } catch (err) {
      setMessage({ type: 'error', text: errorText(err, 'No se pudo desconectar Mercado Pago.') });
    } finally {
      setConfirmDisconnect(false);
    }
  };

  const connected = !!account?.connected;

  return (
    <SectionCard
      title="Cobros con tarjeta"
      subtitle="Tu terminal Mercado Pago Point cobra el total que manda TRUE POS: sin teclear montos"
      action={connected ? <Chip size="small" color="success" label="Mercado Pago conectado" /> : undefined}
    >
      {message && <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}
      {!account && <Skeleton variant="rounded" height={80} />}

      {account && !account.available && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>La conexión con Mercado Pago estará disponible pronto.</Typography>
      )}

      {account?.available && !connected && (
        <Stack spacing={2}>
          {account.problem && <Alert severity="warning">{account.problem}</Alert>}
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Conecta la cuenta de Mercado Pago de tu negocio: inicias sesión en Mercado Pago y autorizas a TRUE. TRUE nunca ve tu
            contraseña y el dinero llega directo a tu cuenta.
          </Typography>
          <Box>
            <Button variant="contained" startIcon={busy === 'connect' ? <CircularProgress size={18} color="inherit" /> : <CreditCardOutlinedIcon />}
              onClick={connect} disabled={busy === 'connect'}>
              {account.problem ? 'Volver a conectar Mercado Pago' : 'Conectar Mercado Pago'}
            </Button>
          </Box>
        </Stack>
      )}

      {connected && (
        <Stack spacing={2}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2">Terminales</Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadTerminals} disabled={terminals === null}>Actualizar</Button>
          </Stack>
          {terminals === null && <Skeleton variant="rounded" height={64} />}
          {terminals?.length === 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No hay terminales en tu cuenta de Mercado Pago. Vincula tu Point desde la app de Mercado Pago (escanea el QR de la
              terminal) y presiona Actualizar.
            </Typography>
          )}
          {terminals?.map(terminal => {
            const mode = MODES[terminal.operating_mode] ?? { label: terminal.operating_mode || 'Sin modo', color: 'default' as const };
            return (
              <Stack key={terminal.id} direction={{ xs: 'column', sm: 'row' }} spacing={2}
                sx={{ p: 2, borderRadius: 2, border: `1px solid ${TRUE_COLORS.borderSolid}`, alignItems: { sm: 'center' } }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(20,37,44,0.06)', color: TRUE_COLORS.primary, flexShrink: 0 }}>
                  <PointOfSaleOutlinedIcon />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{terminal.model}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>Serie {terminal.serial}</Typography>
                  <Chip size="small" color={mode.color} variant={mode.color === 'default' ? 'outlined' : 'filled'} label={mode.label} />
                </Box>
                <TextField select size="small" label="Cobra para" value={terminal.checkout ?? ''} sx={{ minWidth: 220 }}
                  disabled={busy === terminal.id} onChange={e => assign(terminal, e.target.value)}>
                  <MenuItem value="">Ninguna caja (cobra sola)</MenuItem>
                  {registers.map(register => <MenuItem key={register.id} value={register.id}>{register.label}</MenuItem>)}
                </TextField>
              </Stack>
            );
          })}
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Al elegir una caja, la terminal solo cobra lo que manda TRUE POS desde esa caja. Para volver a teclear montos en la terminal,
            elige «Ninguna caja».
          </Typography>
          <Box>
            <Button color="error" size="small" onClick={() => setConfirmDisconnect(true)}>Desconectar Mercado Pago</Button>
          </Box>
        </Stack>
      )}

      <ConfirmDialog
        open={confirmDisconnect}
        danger
        title="¿Desconectar Mercado Pago?"
        description="TRUE POS dejará de cobrar con tus terminales y estas volverán a cobrar solas. Puedes conectarla de nuevo cuando quieras."
        confirmLabel="Desconectar"
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={disconnect}
      />
    </SectionCard>
  );
};

export default CardPayments;
